import { Component } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { Box, Grid, Header, Input, Pagination, Table, Button, Alert, DateRangePicker } from "@cloudscape-design/components";
import Preferences from "@cloudscape-design/components/collection-preferences";
import Multiselect from "@cloudscape-design/components/multiselect";
import { analyticsBannerInfoModalStore, analyticsBannersStore } from "../stores/analyticsBanners";
import { configStore } from "../stores/config";
import { userStore } from "../stores/access";

const formatCtr = (value) => {
	const numericValue = Number(value);

	return `${Number.isFinite(numericValue) ? numericValue.toFixed(2) : "0.00"}%`;
};

export class BannersAnalyticsBase extends Component {
	static defaultProps = {
		configKey: "analyticsBanners",
		perPageStorageKey: "analyticsBanners.perPage",
		title: "Banners Analytics",
	};

	constructor(props) {
		super(props);

		this.productsDebounceTimer = null;
		this.state = {
			searchValue: this.props.analyticsBanners.currentSearch,
		};
	}

	loadAnalyticsList() {
		if (this.props.loadAnalyticsList) {
			this.props.loadAnalyticsList();
		} else {
			this.props.Interface.loadBannerAnalyticsList();
		}
	}

	handleKeyPress = (event) => {
		if (event.detail.key === "Enter") {
			this.props.setAnalyticsBanners({
				...this.props.analyticsBanners,
				currentSearch: this.state.searchValue,
				pagination: {
					...this.props.analyticsBanners.pagination,
					current: 1,
				},
			});
			setTimeout(() => {
				this.loadAnalyticsList();
			}, 0);
		}
	};

	handleInputDebounceChange = (input) => {
		this.setState({
			searchValue: input.detail.value,
		});

		if (input.detail.value.length === 0 && this.props.analyticsBanners.currentSearch.length > 0) {
			this.props.setAnalyticsBanners({
				...this.props.analyticsBanners,
				currentSearch: "",
				pagination: {
					...this.props.analyticsBanners.pagination,
					current: 1,
				},
			});
			setTimeout(() => this.loadAnalyticsList(), 250);
		}
	};

	componentDidMount() {
		this.loadAnalyticsList();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.analyticsBanners.sorting.products !== this.props.analyticsBanners.sorting.products) {
			clearTimeout(this.productsDebounceTimer);

			this.productsDebounceTimer = setTimeout(() => {
				this.loadAnalyticsList();
			}, 500);
		}
	}

	render() {
		return (
			<>
				{this.props.analyticsBanners.loaded && this.props.analyticsBanners.error.text.length ? (
					<Box
						margin={{ bottom: "m" }}
						children={
							<Alert
								type="error"
								header="Error loading data:"
								children={
									<>
										<Box variant="p" children={this.props.analyticsBanners.error.text} />
										<hr />
										<Box
											variant="p"
											children={
												<>
													request error code: {this.props.analyticsBanners.error.code} | request timestamp: {this.props.analyticsBanners.error.timestamp}
												</>
											}
										/>
									</>
								}
							/>
						}
					/>
				) : (
					<></>
				)}
				<Box>
					<Table
						stickyHeader="true"
						trackBy="id"
						loading={!this.props.analyticsBanners.loaded}
						sortingColumn={{ sortingField: this.props.analyticsBanners.sorting.sortingColumn.column }}
						sortingDescending={this.props.analyticsBanners.sorting.isDescending}
						onSortingChange={(sorting) => {
							this.props.setAnalyticsBanners({
								...this.props.analyticsBanners,
								sorting: {
									...this.props.analyticsBanners.sorting,
									sortingColumn: {
										order: !sorting.detail.isDescending ? "ASC" : "DESC",
										column: sorting.detail.sortingColumn.sortingField,
									},
									isDescending: sorting.detail.isDescending,
								},
							});

							setTimeout(() => {
								this.loadAnalyticsList();
							}, 250);
						}}
						columnDefinitions={[
							{ id: "id", header: "ID", cell: (item) => item.id },
							{ id: "product", header: "Product", sortingField: "product", cell: (item) => item.product },
							{ id: "date", header: "Date", sortingField: "date", cell: (item) => new Date(item.date).toLocaleDateString() },
							{
								id: "name",
								header: "Banner",
								sortingField: "name",
								cell: (item) => (
									<Button
										variant="link"
										onClick={() => {
											setTimeout(() => {
												this.props.setAnalyticsBannerInfoModal((prev) => ({
													...prev,
													currentBanner: item.name,
													show: true,
												}));
											}, 0);
										}}>
										{item.name}
									</Button>
								),
							},
							{ id: "view", header: "View", sortingField: "view", cell: (item) => item.view },
							{ id: "click", header: "Click", sortingField: "click", cell: (item) => item.click },
							{
								id: "ctr",
								header: "CTR",
								cell: (item) => formatCtr(item.ctr),
							},
						]}
						items={this.props.analyticsBanners.data}
						pagination={
							<Pagination
								disabled={!this.props.analyticsBanners.loaded}
								currentPageIndex={this.props.analyticsBanners.pagination.current}
								pagesCount={this.props.analyticsBanners.pagination.total}
								onChange={(page) => {
									this.props.setAnalyticsBanners({ ...this.props.analyticsBanners, pagination: { ...this.props.analyticsBanners.pagination, current: page.detail.currentPageIndex } });
									setTimeout(() => {
										this.loadAnalyticsList();
									}, 250);
								}}
							/>
						}
						preferences={
							<Preferences
								cancelLabel="Cancel"
								confirmLabel="Confirm"
								pageSizePreference={{ title: "Logs per page", options: this.props.config[this.props.configKey].preferences.perPage }}
								preferences={{ pageSize: this.props.config[this.props.configKey].perPage, custom: this.props.analyticsBanners.sorting.products }}
								title="Preferences"
								onConfirm={(preferences) => {
									localStorage.setItem(this.props.perPageStorageKey, preferences.detail.pageSize);
									this.props.setConfig({ ...this.props.config, [this.props.configKey]: { ...this.props.config[this.props.configKey], perPage: preferences.detail.pageSize } });

									this.props.setAnalyticsBanners((prev) => ({
										...prev,
										sorting: {
											...prev.sorting,
											products: preferences.detail.custom,
										},
									}));

									setTimeout(() => {
										this.loadAnalyticsList();
									}, 250);
								}}
							/>
						}
						filter={
							<Box
								margin={{ bottom: "m" }}
								children={
									<Grid
										gridDefinition={[{ colspan: "auto" }, { colspan: "auto" }]}
										children={
											<>
												<Input
													disableBrowserAutocorrect={true}
													placeholder="Banner name"
													inputMode="search"
													type="search"
													onKeyDown={this.handleKeyPress}
													disabled={!this.props.analyticsBanners.loaded}
													value={this.state.searchValue}
													onChange={(input) => {
														this.handleInputDebounceChange(input);
													}}
												/>

												<Box variant="div">
													<Multiselect
														selectedOptions={this.props.analyticsBanners.sorting.products}
														placeholder="Chose product"
														inlineTokens
														hideTokens
														ariaLabel="Option 1, Option 2, Option 3, Option 4, Option 5, Option 6 selected"
														disabled={!this.props.analyticsBanners.loaded || this.props.brandList.length === 0}
														onChange={({ detail }) => {
															this.props.setAnalyticsBanners((prev) => ({
																...prev,
																pagination: {
																	...this.props.analyticsBanners.pagination,
																	current: 1,
																},
																sorting: {
																	...prev.sorting,
																	products: detail.selectedOptions,
																},
															}));
														}}
														options={this.props.brandList.map((item) => ({ value: item.id, label: item.name }))}
													/>
												</Box>

												<DateRangePicker
													onChange={({ detail }) => {
														this.props.setAnalyticsBanners((prev) => ({
															...prev,
															pagination: { ...prev.pagination, current: 1 },
															sorting: {
																...prev.sorting,
																date: detail.value || {
																	type: "absolute",
																	startDate: false,
																	endDate: false,
																},
															},
														}));
														setTimeout(() => {
															this.loadAnalyticsList();
														}, 250);
													}}
													value={this.props.analyticsBanners.sorting.date}
													isValidRange={(range) => {
														if (range.type === "absolute") {
															const [startDateWithoutTime] = range.startDate.split("T");
															const [endDateWithoutTime] = range.endDate.split("T");
															if (!startDateWithoutTime || !endDateWithoutTime) {
																return {
																	valid: false,
																	errorMessage: "The selected date range is incomplete. Select a start and end date for the date range.",
																};
															}
															if (new Date(range.startDate) - new Date(range.endDate) > 0) {
																return {
																	valid: false,
																	errorMessage: "The selected date range is invalid. The start date must be before the end date.",
																};
															}
														} else {
															return { valid: false, errorMessage: "Only Absolute mode" };
														}
														return { valid: true };
													}}
													i18nStrings={{
														todayAriaLabel: "Today",
														nextMonthAriaLabel: "Next month",
														previousMonthAriaLabel: "Previous month",
														customRelativeRangeDurationLabel: "Duration",
														customRelativeRangeDurationPlaceholder: "Enter duration",
														customRelativeRangeOptionLabel: "Custom range",
														customRelativeRangeOptionDescription: "Define a custom range in the past",
														customRelativeRangeUnitLabel: "Unit of time",
														formatRelativeRange: ({ startDate, endDate }) => {
															const start = new Date(startDate);
															const end = new Date(endDate);

															const locale = "en-GB";
															const dateOptions = { year: "numeric", month: "long", day: "numeric" };
															const timeOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit" };

															const formattedStart = start.toLocaleDateString(locale, dateOptions) + " " + start.toLocaleTimeString(locale, timeOptions);
															const formattedEnd = end.toLocaleDateString(locale, dateOptions) + " " + end.toLocaleTimeString(locale, timeOptions);

															return `${formattedStart} - ${formattedEnd}`;
														},
														formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
														dateTimeConstraintText: "Date and time must be within the selected range.",
														renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
														labelRelativeRange: "Relative range",
														labelAbsoluteRange: "Absolute range",
														dateTimePickerAriaLabel: "Date and time picker",
														dateTimePickerLabel: "Date and Time",
														clearButtonAriaLabel: "Clear date range",
														relativeModeTitle: "Relative mode",
														absoluteModeTitle: "Absolute mode",
														cancelButtonLabel: "Cancel",
														applyButtonLabel: "Apply",
														clearButtonLabel: "Clear and dismiss",
													}}
													dateOnly
													expandToViewport
													hideTimeOffset
													disabled={!this.props.analyticsBanners.loaded}
													placeholder="from/to"
												/>
											</>
										}
									/>
								}
							/>
						}
						header={<Box margin={{ bottom: "m" }} children={<Header children={this.props.title} counter={<>({this.props.analyticsBanners.pagination.count})</>} />} />}
						footer={
							this.props.analyticsBanners.loaded && (
								<Grid gridDefinition={[{ colspan: 2 }, { colspan: 10 }]}>
									<Box variant="p">Total</Box>
									<Box size="s" float="right">
										<Box variant="span" display="inline-block" margin={{ left: "s" }}>
											View: {this.props.analyticsBanners.totalView}
										</Box>
										<Box variant="span" display="inline-block" margin={{ left: "s" }}>
											Click: {this.props.analyticsBanners.totalClick}
										</Box>
										<Box variant="span" display="inline-block" margin={{ left: "s" }}>
											CTR: {formatCtr(this.props.analyticsBanners.totalCtr)}
										</Box>
									</Box>
								</Grid>
							)
						}
						empty="No banners found with requested filter"
					/>
				</Box>
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const analyticsBanners = useRecoilValue(analyticsBannersStore);
		const setAnalyticsBanners = useSetRecoilState(analyticsBannersStore);
		const setAnalyticsBannerInfoModal = useSetRecoilState(analyticsBannerInfoModalStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);
		const user = useRecoilValue(userStore);
		const brandList = user.brandsList || [];

		return <Component {...props} brandList={brandList} analyticsBanners={analyticsBanners} setAnalyticsBannerInfoModal={setAnalyticsBannerInfoModal} setAnalyticsBanners={setAnalyticsBanners} config={config} setConfig={setConfig} />;
	};
}

export default withRecoil(BannersAnalyticsBase);
